// src/App.js
import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { SignInButton, SignedIn, SignedOut, UserButton, useAuth, useClerk } from "@clerk/clerk-react";
import "bootstrap/dist/css/bootstrap.min.css";
import Chatbot from "react-chatbot-kit";
import "react-chatbot-kit/build/main.css";

import config from "./chatbot/config";
import MessageParser from "./chatbot/MessageParser";
import ActionProvider from "./chatbot/ActionProvider";

import "./App.css";
import optibotLogo from "./Optibot White Logo.svg";

// Page components
import DataSourcesPage from "./DataSourcesPage";
import AdminPage from "./AdminPage";
import PaywallModal from "./components/PaywallModal";
import TrialBanner from "./TrialBanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "https://optibot-procurement.onrender.com";

/* ===========================
   INLINE FILTER DATA + MAPS
   =========================== */

const AGENCY_TO_NAICS = {
  "Department of Agriculture": ["11", "21", "22", "23", "31", "32", "33", "42", "44", "48", "49", "51", "52", "53", "54", "56", "61", "62", "71", "72", "81", "92"],
  "Department of Commerce": ["11", "22", "23", "31", "32", "33", "42", "44", "45", "48", "49", "51", "52", "53", "54", "56", "61", "62", "71", "72", "81", "92"],
  "Department of Defense": ["33", "48", "51", "54", "56", "61"],
  "Department of Education": ["33", "42", "44", "51", "52", "54", "56", "61", "72", "81"],
  "Department of Energy": ["21", "22", "23", "31", "32", "33", "42", "48", "49", "51", "52", "53", "54", "56", "61", "62", "81", "92"],
  "Department of Health and Human Services": ["11", "22", "23", "31", "32", "33", "42", "44", "45", "48", "49", "51", "52", "53", "54", "56", "61", "62", "72", "81", "92"],
  "Department of Homeland Security": ["22", "23", "31", "32", "33", "48", "49", "51", "52", "53", "54", "56", "61", "62", "71", "72", "81"],
  "Department of Housing and Urban Development": ["22", "23", "33", "42", "51", "52", "53", "54", "56", "61", "81"],
  "Department of Justice": ["11", "22", "23", "31", "32", "33", "42", "48", "49", "51", "53", "54", "56", "61", "62", "81", "92"],
  "Department of Labor": ["22", "23", "32", "33", "42", "48", "51", "52", "53", "54", "56", "61", "62", "72", "81"],
  "Department of State": ["22", "23", "31", "32", "33", "42", "44", "45", "48", "49", "51", "52", "53", "54", "56", "61", "62", "72", "81", "92"],
  "Department of Transportation": ["22", "23", "31", "33", "42", "48", "51", "52", "54", "56", "61", "62", "71", "72", "81", "92"],
  "Department of the Interior": ["11", "21", "22", "23", "31", "32", "33", "42", "44", "45", "48", "49", "51", "53", "54", "56", "61", "62", "71", "72", "81", "92"],
  "Environmental Protection Agency": ["11", "22", "23", "32", "33", "42", "44", "48", "49", "51", "52", "53", "54", "56", "61", "62", "72", "81", "92"],
  "General Services Administration": ["22", "23", "31", "33", "42", "44", "48", "49", "51", "52", "53", "54", "56", "61", "81", "92"],
  "National Aeronautics and Space Administration": ["23", "32", "33", "48", "51", "54", "56", "62", "81", "92"],
  "National Science Foundation": [],
  "Nuclear Regulatory Commission": ["33", "42", "51", "54", "56", "61", "92"],
  "Office of Personnel Management": ["51", "52", "53", "54", "56", "61"],
  "Social Security Administration": ["33", "49", "51", "54", "56"]
};

const ALL_AGENCIES = Object.keys(AGENCY_TO_NAICS);
const ALL_NAICS = [...new Set(Object.values(AGENCY_TO_NAICS).flat())].sort();
const NAICS_TO_AGENCIES = Object.fromEntries(
  ALL_NAICS.map(code => [code, ALL_AGENCIES.filter(agency => AGENCY_TO_NAICS[agency].includes(code))])
);

/* ===========================
   NAICS labels + helpers   =========================== */

const NAICS_LABELS = {
  "11": "Agriculture, Forestry, Fishing & Hunting",
  "21": "Mining, Quarrying, Oil & Gas Extraction",
  "22": "Utilities",
  "23": "Construction",
  "31": "Manufacturing",
  "32": "Manufacturing",
  "33": "Manufacturing",
  "42": "Wholesale Trade",
  "44": "Retail Trade",
  "45": "Retail Trade",
  "48": "Transportation & Warehousing",
  "49": "Transportation & Warehousing",
  "51": "Information",
  "52": "Finance & Insurance",
  "53": "Real Estate & Rental & Leasing",
  "54": "Professional, Scientific & Technical Services",
  "55": "Management of Companies & Enterprises",
  "56": "Admin & Support, Waste Mgmt & Remediation",
  "61": "Educational Services",
  "62": "Health Care & Social Assistance",
  "71": "Arts, Entertainment & Recreation",
  "72": "Accommodation & Food Services",
  "81": "Other Services (except Public Administration)",
  "92": "Public Administration",
  // Extra codes shown in UI:
  "15": "Other",
  "24": "Other",
  "25": "Other",
  "34": "Other",
  "36": "Other",
  "64": "Other",
  "99": "Unclassified"
};

const formatNaicsLabel = (code) => {
  if (!code) return "Select the applicable NAICS";
  const raw = NAICS_LABELS[code];
  const label = (typeof raw === "string" ? raw.trim() : "Other");
  return `${code} - ${label}`;
};

/* ===========================
   SEARCH / MATCH HELPERS
   =========================== */

const normalize = (s = "") =>
  s.toString().toLowerCase().replace(/[^a-z0-9\s&/.-]/g, "").trim();

const AGENCY_SYNONYMS = {
  "hhs": "Department of Health and Human Services",
  "health and human services": "Department of Health and Human Services",
  "cms": "Department of Health and Human Services",
  "doe": "Department of Energy",
  "energy": "Department of Energy",
  "ed": "Department of Education",
  "education": "Department of Education",
  "commerce": "Department of Commerce",
  "interior": "Department of the Interior",
  "gsa": "General Services Administration",
  "dol": "Department of Labor",
  "va": "Department of Veterans Affairs",
  "nrc": "Nuclear Regulatory Commission",
  "sba": "Small Business Administration",
  "opm": "Office of Personnel Management",
  "dod": "Department of Defense",
  "dhs": "Department of Homeland Security",
  "dot": "Department of Transportation",
  "epa": "Environmental Protection Agency",
  "state": "Department of State",
  "treasury": "Department of Treasury",
  "ssa": "Social Security Administration",
  "nasa": "National Aeronautics and Space Administration",
  "social security": "Social Security Administration",
  "dept of state": "Department of State",
  "state department": "Department of State",
};

const NAICS_SYNONYMS = {
  /* 11 - Agriculture, Forestry, Fishing & Hunting */
  "agriculture": ["11"],
  "agricultural": ["11"],
  "agribusiness": ["11"],
  "farming": ["11"],
  "farm": ["11"],
  "farms": ["11"],
  "ranch": ["11"],
  "ranching": ["11"],
  "crop": ["11"],
  "crops": ["11"],
  "orchard": ["11"],
  "vineyard": ["11"],
  "livestock": ["11"],
  "dairy": ["11"],
  "poultry": ["11"],
  "hog": ["11"],
  "swine": ["11"],
  "aquaculture": ["11"],
  "fishing": ["11"],
  "fishery": ["11"],
  "forestry": ["11"],
  "logging": ["11"],
  "timber": ["11"],
  "horticulture": ["11"],
  "nursery": ["11"],

  /* 21 - Mining, Quarrying, Oil & Gas Extraction */
  "mining": ["21"],
  "mine": ["21"],
  "quarry": ["21"],
  "quarrying": ["21"],
  "pit": ["21"],
  "aggregate": ["21"],
  "sand": ["21"],
  "gravel": ["21"],
  "coal": ["21"],
  "mineral": ["21"],
  "ore": ["21"],
  "oil": ["21"],
  "gas": ["21"],
  "natural gas": ["21"],
  "drilling": ["21"],
  "well services": ["21"],
  "fracking": ["21"],
  "extraction": ["21"],

  /* 22 - Utilities */
  "utilities": ["22"],
  "utility": ["22"],
  "power": ["22"],
  "electric": ["22"],
  "electricity": ["22"],
  "grid": ["22"],
  "energy utility": ["22"],
  "water": ["22"],
  "wastewater": ["22"],
  "sewage": ["22"],
  "sewer": ["22"],
  "stormwater": ["22"],
  "gas utility": ["22"],
  "pipeline utility": ["22"],
  "trash utility": ["22"],

  /* 23 - Construction */
  "construction": ["23"],
  "brick": ["23"],
  "bricks": ["23"],
  "build": ["23"],
  "building": ["23"],
  "builder": ["23"],
  "civil": ["23"],
  "infrastructure": ["23"],
  "road": ["23"],
  "roads": ["23"],
  "highway": ["23"],
  "highways": ["23"],
  "bridge": ["23"],
  "bridges": ["23"],
  "paving": ["23"],
  "asphalt": ["23"],
  "concrete": ["23"],
  "masonry": ["23"],
  "carpentry": ["23"],
  "roofing": ["23"],
  "siding": ["23"],
  "drywall": ["23"],
  "flooring": ["23"],
  "glazing": ["23"],
  "plumbing": ["23"],
  "electrical": ["23"],
  "mechanical": ["23"],
  "hvac": ["23"],
  "fire protection": ["23"],
  "low voltage": ["23"],
  "sitework": ["23"],
  "earthwork": ["23"],
  "excavation": ["23"],
  "demolition": ["23"],
  "renovation": ["23"],
  "remodeling": ["23"],
  "tenant improvement": ["23"],
  "gc": ["23"],

  /* 31-33 - Manufacturing */
  "manufacturing": ["31", "32", "33"],
  "manufacture": ["31", "32", "33"],
  "fabrication": ["31", "32", "33"],
  "fabricate": ["31", "32", "33"],
  "machining": ["31", "32", "33"],
  "tooling": ["31", "32", "33"],
  "assembly": ["31", "32", "33"],
  "production": ["31", "32", "33"],
  "plant": ["31", "32", "33"],
  "factory": ["31", "32", "33"],
  "industrial": ["31", "32", "33"],
  "metalworking": ["31", "32", "33"],
  "welding": ["31", "32", "33"],
  "foundry": ["31", "32", "33"],
  "forging": ["31", "32", "33"],
  "casting": ["31", "32", "33"],
  "textiles": ["31", "32", "33"],
  "apparel": ["31", "32", "33"],
  "paper mill": ["31", "32", "33"],
  "plastics": ["31", "32", "33"],
  "rubber": ["31", "32", "33"],
  "chemical manufacturing": ["31", "32", "33"],
  "pharmaceutical manufacturing": ["31", "32", "33"],
  "food processing": ["31", "32", "33"],
  "electronics manufacturing": ["31", "32", "33"],
  "medical device": ["31", "32", "33"],
  "3d printing": ["31", "32", "33"],

  /* 42 - Wholesale Trade */
  "wholesale": ["42"],
  "wholesaler": ["42"],
  "distribution": ["42"],
  "distributor": ["42"],
  "bulk supply": ["42"],
  "b2b supply": ["42"],
  "trade wholesaler": ["42"],

  /* 44-45 - Retail Trade */
  "retail": ["44", "45"],
  "store": ["44", "45"],
  "shop": ["44", "45"],
  "shops": ["44", "45"],
  "merchant": ["44", "45"],
  "point of sale": ["44", "45"],
  "pos": ["44", "45"],
  "ecommerce": ["44", "45"],
  "online store": ["44", "45"],
  "boutique": ["44", "45"],
  "catalog": ["44", "45"],
  "big box": ["44", "45"],

  /* 48-49 - Transportation & Warehousing */
  "transportation": ["48", "49"],
  "transport": ["48", "49"],
  "logistics": ["48", "49"],
  "supply chain": ["48", "49"],
  "freight": ["48", "49"],
  "trucking": ["48", "49"],
  "truck": ["48", "49"],
  "rail": ["48", "49"],
  "railroad": ["48", "49"],
  "air cargo": ["48", "49"],
  "airline cargo": ["48", "49"],
  "maritime": ["48", "49"],
  "shipping": ["48", "49"],
  "port": ["48", "49"],
  "harbor": ["48", "49"],
  "warehouse": ["49"],
  "warehousing": ["49"],
  "3pl": ["48", "49"],
  "courier": ["49"],
  "delivery": ["49"],
  "parcel": ["49"],
  "postal": ["49"],

  /* 51 - Information */
  "information": ["51"],
  "media": ["51"],
  "publishing": ["51"],
  "publisher": ["51"],
  "broadcast": ["51"],
  "broadcasting": ["51"],
  "telecom": ["51"],
  "telecommunications": ["51"],
  "isp": ["51"],
  "streaming": ["51"],
  "film": ["51"],
  "video": ["51"],
  "news": ["51"],
  "data center": ["51"],
  "data hosting": ["51"],
  "hosting": ["51"],
  "cloud": ["51"],
  "saas": ["51"],
  "paaS": ["51"],
  "iaas": ["51"],
  "content platform": ["51"],

  /* 52 - Finance & Insurance */
  "finance": ["52"],
  "financial": ["52"],
  "bank": ["52"],
  "banking": ["52"],
  "credit": ["52"],
  "loans": ["52"],
  "mortgage": ["52"],
  "investment": ["52"],
  "securities": ["52"],
  "brokerage": ["52"],
  "asset management": ["52"],
  "wealth management": ["52"],
  "insurance": ["52"],
  "insurer": ["52"],
  "underwriting": ["52"],
  "actuarial": ["52"],
  "risk management": ["52"],
  "fintech": ["52"],

  /* 53 - Real Estate & Rental & Leasing */
  "real estate": ["53"],
  "property": ["53"],
  "property management": ["53"],
  "leasing": ["53"],
  "rental": ["53"],
  "rentals": ["53"],
  "broker": ["53"],
  "realtor": ["53"],
  "facility lease": ["53"],
  "equipment leasing": ["53"],
  "self storage": ["53"],

  /* 54 - Professional, Scientific & Technical Services */
  "professional services": ["54"],
  "consulting": ["54"],
  "management consulting": ["54"],
  "strategy consulting": ["54"],
  "it services": ["54"],
  "software development": ["54"],
  "software engineering": ["54"],
  "systems integration": ["54"],
  "cybersecurity": ["54"],
  "data science": ["54"],
  "analytics": ["54"],
  "ai": ["54"],
  "ml": ["54"],
  "cloud consulting": ["54"],
  "devops": ["54"],
  "ui ux": ["54"],
  "architecture": ["54"],
  "architectural": ["54"],
  "engineering": ["54"],
  "civil engineering": ["54"],
  "structural engineering": ["54"],
  "mechanical engineering": ["54"],
  "electrical engineering": ["54"],
  "geospatial": ["54"],
  "surveying": ["54"],
  "mapping": ["54"],
  "gis": ["54"],
  "scientific": ["54"],
  "r&d": ["54"],
  "laboratory": ["54"],
  "biotech": ["54"],
  "legal": ["54"],
  "law": ["54"],
  "accounting": ["54"],
  "audit": ["54"],
  "tax": ["54"],

  /* 56 - Administrative & Support; Waste Mgmt & Remediation */
  "admin": ["56"],
  "administrative": ["56"],
  "business support": ["56"],
  "back office": ["56"],
  "bpo": ["56"],
  "call center": ["56"],
  "contact center": ["56"],
  "answering services": ["56"],
  "staffing": ["56"],
  "temporary staffing": ["56"],
  "recruiting": ["56"],
  "talent acquisition": ["56"],
  "janitorial": ["56"],
  "cleaning": ["56"],
  "custodial": ["56"],
  "housekeeping": ["56"],
  "landscaping": ["56"],
  "grounds": ["56"],
  "pest control": ["56"],
  "security guard": ["56"],
  "facilities support": ["56"],
  "travel agency": ["56"],
  "event planning": ["56"],
  "remediation": ["56"],
  "hazardous waste": ["56"],
  "environmental cleanup": ["56"],
  "abatement": ["56"],

  /* 61 - Educational Services */
  "education": ["61"],
  "educational": ["61"],
  "training": ["61"],
  "workforce development": ["61"],
  "e-learning": ["61"],
  "online learning": ["61"],
  "curriculum": ["61"],
  "instruction": ["61"],
  "tutoring": ["61"],
  "school": ["61"],
  "university": ["61"],
  "college": ["61"],

  /* 62 - Health Care & Social Assistance */
  "health": ["62"],
  "healthcare": ["62"],
  "medical": ["62"],
  "hospital": ["62"],
  "clinic": ["62"],
  "outpatient": ["62"],
  "primary care": ["62"],
  "behavioral health": ["62"],
  "mental health": ["62"],
  "substance abuse": ["62"],
  "dental": ["62"],
  "nursing": ["62"],
  "home health": ["62"],
  "telehealth": ["62"],
  "public health": ["62"],
  "ems": ["62"],
  "ambulance": ["62"],
  "pharmacy": ["62"],
  "social": ["62"],
  "social services": ["62"],
  "child care": ["62"],
  "elder care": ["62"],

  /* 71 - Arts, Entertainment & Recreation */
  "arts": ["71"],
  "art": ["71"],
  "entertainment": ["71"],
  "theater": ["71"],
  "performing arts": ["71"],
  "music": ["71"],
  "concert": ["71"],
  "festival": ["71"],
  "museum": ["71"],
  "gallery": ["71"],
  "zoo": ["71"],
  "aquarium": ["71"],
  "sports": ["71"],
  "recreation": ["71"],
  "fitness": ["71"],
  "gym": ["71"],
  "amusement": ["71"],
  "park": ["71"],
  "events": ["71"],

  /* 72 - Accommodation & Food Services */
  "accommodation": ["72"],
  "hospitality": ["72"],
  "hotel": ["72"],
  "lodging": ["72"],
  "motel": ["72"],
  "resort": ["72"],
  "restaurant": ["72"],
  "food service": ["72"],
  "food services": ["72"],
  "catering": ["72"],
  "cafeteria": ["72"],
  "bar": ["72"],
  "coffee shop": ["72"],

  /* 81 - Other Services (except Public Administration) */
  "other services": ["81"],
  "repair": ["81"],
  "maintenance": ["81"],
  "auto repair": ["81"],
  "equipment repair": ["81"],
  "appliance repair": ["81"],
  "barber": ["81"],
  "salon": ["81"],
  "hair": ["81"],
  "nail": ["81"],
  "dry cleaning": ["81"],
  "laundry": ["81"],
  "funeral": ["81"],
  "religious organizations": ["81"],
  "civic organizations": ["81"],
  "nonprofit": ["81"],

  /* 92 - Public Administration */
  "public administration": ["92"],
  "government": ["92"],
  "federal": ["92"],
  "state government": ["92"],
  "local government": ["92"],
  "municipal": ["92"],
  "county": ["92"],
  "city": ["92"],
  "township": ["92"],
  "regulatory": ["92"],
  "public safety": ["92"],
  "emergency management": ["92"],

  /* Keep a few handy aliases you already had */
  "wholesale trade": ["42"],
  "retail trade": ["44", "45"],
  "transportation and warehousing": ["48", "49"],
  "professional services": ["54"],

  /* Optional extras matching the UI's custom codes (map to their buckets) */
  "misc": ["15"],
  "miscellaneous": ["15"],
  "unclassified": ["99"],
};

const naicsOptionMatches = (code, q) => {
  if (!q) return true;
  const query = normalize(q);
  if (!query) return true;
  const label = NAICS_LABELS[code] || "Other";
  if (normalize(code).includes(query)) return true;
  if (normalize(label).includes(query)) return true;
  for (const [key, codes] of Object.entries(NAICS_SYNONYMS)) {
    if (query.includes(normalize(key)) && codes.includes(code)) return true;
  }
  return false;
};

const agencyOptionMatches = (agency, q) => {
  if (!q) return true;
  const query = normalize(q);
  if (!query) return true;
  if (normalize(agency).includes(query)) return true;
  for (const [key, canonical] of Object.entries(AGENCY_SYNONYMS)) {
    if (query.includes(normalize(key)) && canonical === agency) return true;
  }
  return false;
};

/* ===========================
   Reusable Combobox Component
   =========================== */

function ComboBox({
  id,
  placeholder,
  value,          // display value in the input
  setValue,       // setter for input text
  onSelect,       // called with chosen item's raw value
  options,        // [{value, label}]
  filterFn,       // (optionValue, query) -> boolean
  resetOption,    // { value: "", label: "Select ..." }  <-- NEW
  menuMaxHeight = 420,
  numericTabular = false
}) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const containerRef = useRef(null);

  const filteredCore = useMemo(() => {
    return options.filter(opt => filterFn(opt.value, value));
  }, [options, value, filterFn]);

  // Always show reset option at the top
  const filtered = useMemo(() => {
    const list = [...filteredCore];
    if (resetOption) {
      // Only add one instance and always at top
      return [resetOption, ...list];
    }
    return list;
  }, [filteredCore, resetOption]);

  // Close on outside click
  useEffect(() => {
    function onDocClick(e) {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleFocus = () => setOpen(true);
  const handleToggle = () => setOpen(o => !o);

  const handleChange = (e) => {
    setValue(e.target.value);
    if (!open) setOpen(true);
    setHighlight(0);
  };

  const commitSelection = (opt) => {
    if (opt.value === "" && resetOption) {
      // Reset: clear input so placeholder shows
      setValue("");
      onSelect("");
      setOpen(false);
      return;
    }
    setValue(opt.label);
    onSelect(opt.value);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) {
      setOpen(true);
      return;
    }
    if (!open) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight(h => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filtered[highlight]) commitSelection(filtered[highlight]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="position-relative" ref={containerRef}>
      <div className="input-group">
        <input
          id={id}
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onKeyDown={onKeyDown}
          style={numericTabular ? { fontVariantNumeric: "tabular-nums" } : undefined}
          autoComplete="off"
          aria-autocomplete="list"
          aria-expanded={open}
          aria-controls={`${id}-listbox`}
          role="combobox"
        />
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={handleToggle}
          aria-label="Toggle options"
          tabIndex={-1}
        >
          &#9662;
        </button>
      </div>

      {open && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          className="list-group position-absolute w-100 shadow-sm"
          style={{ zIndex: 1000, maxHeight: menuMaxHeight, overflowY: "auto" }}
        >
          {filtered.length === 0 && (
            <li className="list-group-item text-muted">No matches</li>
          )}
          {filtered.map((opt, idx) => (
            <li
              key={`${opt.value || "reset"}-${idx}`}
              role="option"
              aria-selected={idx === highlight}
              className={
                "list-group-item d-flex justify-content-between align-items-center " +
                (idx === highlight ? "active" : "")
              }
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => { e.preventDefault(); commitSelection(opt); }}
            >
              <span>{opt.label}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ===========================
   APP
   =========================== */

// Home component - the main chatbot UI
function Home() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { openSignIn } = useClerk();
  const [trialStartDate, setTrialStartDate] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);
  const [agencyFilter, setAgencyFilter] = useState("");
  const [naicsFilter, setNaicsFilter] = useState("");

  // Available options after mutual filtering
  const [availableAgencies, setAvailableAgencies] = useState(ALL_AGENCIES);
  const [availableNaics, setAvailableNaics] = useState(ALL_NAICS);

  // Single input text shown in each combobox
  const [agencyInput, setAgencyInput] = useState("");
  const [naicsInput, setNaicsInput] = useState("");

  // Build option lists (value + label) from current availability
  const agencyOptions = useMemo(
    () => availableAgencies.map(a => ({ value: a, label: a })),
    [availableAgencies]
  );

  const naicsOptions = useMemo(
    () => availableNaics.map(code => ({ value: code, label: formatNaicsLabel(code) })),
    [availableNaics]
  );

  // Chatbot filter sync
  const actionProvider = new ActionProvider();
  const pushFiltersToBot = (agency, naics) => {
    actionProvider.setFilters(agency ? agency.toLowerCase() : "", naics || "");
  };
  useEffect(() => {
    if (!isLoaded) return;

    if (!isSignedIn) {
      setTrialStartDate(null);
      setTrialExpired(false);
      setHasSubscription(false);
      setIsSuperUser(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const token = await getToken();
        const response = await fetch(`${BACKEND_URL}/api/v1/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error("Unable to load subscription status.");
        const status = await response.json();
        if (cancelled) return;
        setTrialStartDate(status.trialStartDate || null);
        setTrialExpired(Boolean(status.trialExpired));
        setHasSubscription(["active", "trialing"].includes(status.subscription?.status));
        setIsSuperUser(Boolean(status.isSuperUser));
      } catch (error) {
        console.error("Subscription status error:", error);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, isLoaded, isSignedIn]);

  const handleValidatePromo = async (plan, promotionCode) => {
    const token = await getToken();
    const response = await fetch(`${BACKEND_URL}/api/v1/billing/promo/validate`, {
      method: "POST",
      headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      body: JSON.stringify({ plan, promotionCode }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || "Invalid or expired promotion code.");
    return data;
  };

  const handleSubscribe = async (plan, promotionCode) => {
    setIsCheckoutLoading(true);
    try {
      const token = await getToken();
      const response = await fetch(`${BACKEND_URL}/api/v1/billing/checkout`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          ...(promotionCode ? { promotionCode } : {}),
          successUrl: `${window.location.origin}${window.location.pathname}?success=true`,
          cancelUrl: `${window.location.origin}${window.location.pathname}?canceled=true`,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.url) throw new Error(data.message || "Unable to start checkout.");
      window.location.href = data.url;
    } catch (error) {
      alert(error.message || "Unable to start checkout.");
      setIsCheckoutLoading(false);
    }
  };

  const closePaywall = useCallback(() => {
    setShowPaywall(false);
  }, []);

  const validator = () => {
    pushFiltersToBot(agencyFilter, naicsFilter);
    if (!isSignedIn) {
      openSignIn();
      return false;
    }
    if (trialExpired && !hasSubscription && !isSuperUser) {
      setShowPaywall(true);
      return false;
    }
    return true;
  };

  // Apply Agency selection + constrain NAICS
  const applyAgencySelection = (selectedAgency) => {
    setAgencyFilter(selectedAgency);
    setAgencyInput(selectedAgency ? selectedAgency : ""); // clear to show placeholder on reset
    if (!selectedAgency) {
      setAvailableNaics(ALL_NAICS);
      return;
    }
    const allowed = AGENCY_TO_NAICS[selectedAgency] || [];
    setAvailableNaics(allowed);
    if (naicsFilter && !allowed.includes(naicsFilter)) {
      setNaicsFilter("");
      setNaicsInput("");
    }
  };

  // Apply NAICS selection + constrain Agencies
  const applyNaicsSelection = (selectedNaics) => {
    setNaicsFilter(selectedNaics);
    setNaicsInput(selectedNaics ? formatNaicsLabel(selectedNaics) : "");
    if (!selectedNaics) {
      setAvailableAgencies(ALL_AGENCIES);
      return;
    }
    const allowed = NAICS_TO_AGENCIES[selectedNaics] || [];
    setAvailableAgencies(allowed);
    if (agencyFilter && !allowed.includes(agencyFilter)) {
      setAgencyFilter("");
      setAgencyInput("");
    }
  };

  // Keep Chatbot provider in sync
  useEffect(() => {
    pushFiltersToBot(agencyFilter, naicsFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agencyFilter, naicsFilter]);

  // Filter functions for the combobox lists
  const filterAgencies = (agencyName, query) => agencyOptionMatches(agencyName, query);
  const filterNaics = (code, query) => naicsOptionMatches(code, query);

  return (
    <div className="container-fluid p-4">
      <div className="optibot-auth-controls">
        <SignedOut><SignInButton mode="modal"><button className="optibot-sign-in-btn">Sign in</button></SignInButton></SignedOut>
        <SignedIn><button className="btn btn-outline-primary me-2" onClick={() => setShowPaywall(true)}>Upgrade</button><UserButton /></SignedIn>
      </div>
      <TrialBanner
        trialStartDate={trialStartDate}
        trialExpired={trialExpired}
        isSignedIn={isSignedIn}
        hasSubscription={hasSubscription}
        isSuperUser={isSuperUser}
        onSubscribe={() => setShowPaywall(true)}
        isCheckoutLoading={isCheckoutLoading}
        isChatActive={false}
      />
      <div className="header-banner mb-5">
        <div>
          <h1 className="fs-1 mb-1 fw-bold">Procurement Forecast Bot</h1>
        </div>
        <img src={optibotLogo} alt="Optibot Logo" style={{ height: "100px" }} />
      </div>

      <div className="row">
        <div className="col-lg-4 mb-4">
          <div className="card-custom">
            <h5 className="fw-semibold mb-3">Bot Usage Instructions</h5>
            <div className="mb-3">
              <img src="/1.png" alt="Welcome" style={{ width: 24, height: 24, marginRight: 8 }} />
              <strong>Welcome!</strong>
              <p>
                This chatbot helps you explore upcoming federal procurement opportunities using natural language questions.
              </p>
            </div>
            <div className="mb-3">
              <img src="/2.png" alt="Help" style={{ width: 24, height: 24, marginRight: 8 }} />
              <strong>Need help getting started?</strong>
              <p style={{ marginTop: 8 }}>Try these prompts:</p>
              <ul>
                <li>What IT contracts are forecasted by the Department of Homeland Security for FY2025?</li>
                <li>List small business opportunities in health services from HHS.</li>
                <li>Which agencies are planning to procure cloud computing services in the next fiscal year?</li>
              </ul>
            </div>
            <div className="mb-3">
              <strong>Latest Release:</strong>
              <p>Searchable dropdowns with reset options added, plus Last Modified Date and hyperlinks to opportunities.</p>
            </div>
            <Link
              to="/datasources"
              className="btn btn-primary"
            >
              <i className="fas fa-database me-2"></i> View Data Sources
            </Link>

            <div className="aai-branding-section">
              <div className="aai-branding-content">
                <img src="/aai.png" alt="AcquisitionAI Logo" className="aai-logo" />
                <div className="aai-text">
                  <h6 className="aai-title">AcquisitionAI Optibot</h6>
                  <p className="aai-subtitle">Powered by Revelo Software</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="col-lg-8">
          <div className="card-custom d-flex flex-column h-100">
            <div className="filters-container mb-3">
              <div className="filter-group mb-3">
                <label htmlFor="agency-combobox" className="mb-1">Agency</label>
                <ComboBox
                  id="agency-combobox"
                  placeholder="Select an Agency"
                  value={agencyInput}
                  setValue={setAgencyInput}
                  options={agencyOptions}
                  filterFn={filterAgencies}
                  onSelect={applyAgencySelection}
                  resetOption={{ value: "", label: "-- Select an Agency --" }}  // NEW reset
                />
                <small className="text-muted">
                  Type to filter (acronyms like HHS, NASA, VA also work).
                </small>
              </div>

              <div className="filter-group">
                <label htmlFor="naics-combobox" className="mb-1">NAICS</label>
                <ComboBox
                  id="naics-combobox"
                  placeholder="Select the applicable NAICS"
                  value={naicsInput}
                  setValue={setNaicsInput}
                  options={naicsOptions}
                  filterFn={filterNaics}
                  onSelect={applyNaicsSelection}
                  resetOption={{ value: "", label: "-- Select the applicable NAICS --" }} // NEW reset
                  numericTabular
                />
                <small className="text-muted">
                  Type code, label, or synonym (e.g., "construction", "bricks", "23").
                </small>
              </div>
            </div>

            <div className="chatbot-wrapper">
              <Chatbot
                config={config}
                messageParser={MessageParser}
                actionProvider={ActionProvider}
                validator={validator}
              />
            </div>
          </div>
        </div>
      </div>

      <footer className="app-footer">
        <div className="footer-content">
          <div className="footer-disclaimer">
            <strong>Disclaimer:</strong> This chatbot is a Generative AI tool
            developed to assist users in identifying potential federal procurement
            opportunities based on publicly available agency forecast data. While
            Optibot draws from reputable sources such as Acquisition.gov and other
            published forecasts, the information provided may not always reflect the
            most current or official government records.
            <br /><br />
            Verify procurement details with the appropriate federal agency before making
            business or compliance decisions. This tool is in beta and may occasionally
            return incomplete or imprecise responses. Your feedback helps improve accuracy.
          </div>
          <div className="footer-branding">
            <img src="/revelo.png" alt="Revelo Logo" className="revelo-logo" />
            <p className="copyright-text">&copy; 2026 AcquisitionAI | Powered by Revelo</p>
          </div>
        </div>
      </footer>
      <PaywallModal
        isVisible={showPaywall}
        onClose={closePaywall}
        onSubscribe={handleSubscribe}
        onValidatePromo={handleValidatePromo}
        isCheckoutLoading={isCheckoutLoading}
      />
    </div>
  );
}

// Main App component with routing
function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/datasources" element={<DataSourcesPage />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
